import { Flex } from "@minervaproject/fds";
import { useEffect, useRef } from "react";

import { Breakouts } from "./breakouts";
import { CaptionsInput } from "./captions_input";
import { Chat } from "./chat";
import { CurrentAndElapsedTime } from "./current_and_elapsed_time";
import { Directory } from "./directory";
import { Polls } from "./polls";
import { Resources } from "./resources";
import { SentimentRollup } from "./sentiment_rollup";
import { ShuffleButton } from "./shuffle_button";
import { SidebarControls } from "./sidebar_controls";
import { Timeline } from "./timeline";

import { FlightErrorBoundary, SidebarErrorBoundary } from "components/error_boundary";
import { PARTICIPANT_BAR_Z_INDEX } from "components/participant_bar";
import { useCapabilities } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import { Settings } from "settings";
import { SidebarPanelType } from "shared/types";
import { RoleType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { setSidebarPanel } from "store/reducers/local";

import "./sidebar.scss";

function Sidebar() {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const user = useUserProperties(currentUserId, ["role"]);
  const { canControlClassroom, canVisitBreakouts } = useCapabilities();
  const currentPanel = useAppSelector(localSelectors.selectSidebarPanel());
  const currentPanelRef = useRef(currentPanel);
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());

  const positionAdjustment = `${Settings.SIDEBAR_CONTENT_WIDTH}px`;

  useEffect(() => {
    currentPanelRef.current = currentPanel;
  }, [currentPanel]);

  useEffect(() => {
    let panel: SidebarPanelType | null = null;
    if (canControlClassroom) {
      panel = activeBreakoutSessionId ? SidebarPanelType.Breakouts : SidebarPanelType.Timeline;
    } else if (!activeBreakoutSessionId && currentPanelRef.current === SidebarPanelType.Breakouts) {
      panel = user?.role === RoleType.Captioner ? SidebarPanelType.Captions : SidebarPanelType.Chat;
    }
    if (panel) {
      dispatch(setSidebarPanel({ panel }));
    }
  }, [activeBreakoutSessionId, canControlClassroom, canVisitBreakouts, dispatch, user?.role]);

  useEffect(() => {
    if (user?.role === RoleType.Captioner) {
      dispatch(setSidebarPanel({ panel: SidebarPanelType.Captions }));
    } else if (!canControlClassroom && !isInPerson) {
      dispatch(setSidebarPanel({ panel: SidebarPanelType.Chat }));
    }
  }, [canControlClassroom, dispatch, isInPerson, user?.role]);

  return (
    <div
      className="flex"
      data-testid="sidebar"
      style={{
        transform:
          currentPanel === SidebarPanelType.None
            ? `translateX(${positionAdjustment})`
            : "translateX(0px)",
        transition: "transform 0.1s ease-in-out",
        zIndex: PARTICIPANT_BAR_Z_INDEX + 1, // above the participant bar
      }}
    >
      <SidebarControls />
      <div className="flex flex-column bg-black-tint-10">
        {canControlClassroom && <CurrentAndElapsedTime />}
        <FlightErrorBoundary text="These classroom controls cannot be displayed">
          <SentimentRollup />
        </FlightErrorBoundary>
        {canControlClassroom && <ShuffleButton />}
        {currentPanel ? (
          <div className="flex text-white pt3 flex-auto">
            {(() => {
              switch (currentPanel) {
                case SidebarPanelType.Breakouts:
                  return (
                    <SidebarErrorBoundary text="Breakout details cannot be displayed">
                      <Breakouts />
                    </SidebarErrorBoundary>
                  );
                case SidebarPanelType.Captions:
                  return (
                    <SidebarErrorBoundary text="Caption input cannot be displayed" title="Captions">
                      <CaptionsInput />
                    </SidebarErrorBoundary>
                  );
                case SidebarPanelType.Chat:
                  return (
                    <SidebarErrorBoundary
                      text="Chat messages cannot be displayed"
                      title="Class Chat"
                    >
                      <Chat />
                    </SidebarErrorBoundary>
                  );
                case SidebarPanelType.Timeline:
                  return (
                    <SidebarErrorBoundary
                      text="The timeline cannot be displayed"
                      title="Class Timeline"
                    >
                      <Timeline />
                    </SidebarErrorBoundary>
                  );
                case SidebarPanelType.Directory:
                  return (
                    <SidebarErrorBoundary
                      text="The directory cannot be displayed"
                      title="Class Directory"
                    >
                      <Directory />
                    </SidebarErrorBoundary>
                  );
                case SidebarPanelType.Polls:
                  return (
                    <SidebarErrorBoundary text="Poll sidebar cannot be displayed">
                      <Polls />
                    </SidebarErrorBoundary>
                  );
                case SidebarPanelType.Resources:
                  return (
                    <SidebarErrorBoundary text="Resource sidebar cannot be displayed">
                      <Resources />
                    </SidebarErrorBoundary>
                  );
                default:
                  return <Flex className="sidebar-width" data-testid="sidebar-collapsed" />;
              }
            })()}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { Sidebar };
